import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | null} */
  #gridObserver = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#startGridObserver();
    this.#hideSoloInfoCardsOnDesktop();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver?.disconnect();
    this.#gridObserver = null;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');

    // Re-evaluate info-card visibility when crossing breakpoints.
    this.#hideSoloInfoCardsOnDesktop();
  };

  #startGridObserver() {
    const { grid } = this.refs;
    if (!grid || this.#gridObserver) return;

    this.#gridObserver = new MutationObserver(() => {
      // DOM updates happen during filtering and infinite scroll.
      this.#hideSoloInfoCardsOnDesktop();
      // Section morphing can remove attributes like "initialized" without
      // disconnecting/reconnecting this custom element. Ensure it's restored
      // after we evaluate visibility.
      this.setAttribute('initialized', '');
    });

    this.#gridObserver.observe(grid, { childList: true, subtree: false });
  }

  #hideSoloInfoCardsOnDesktop() {
    const { grid } = this.refs;
    if (!grid) return;

    // Only apply on desktop. Also remove any prior hides on mobile.
    const isDesktop = mediaQueryLarge.matches;
    /** @type {HTMLElement[]} */
    const infoCards = Array.from(grid.querySelectorAll('.info-card-grid-item'));

    if (!isDesktop) {
      for (const card of infoCards) card.classList.remove('info-card-grid-item--hide-desktop-row-solo');
      return;
    }

    /** @type {HTMLElement[]} */
    const rowItems = Array.from(grid.children).filter((el) => el instanceof HTMLElement).filter(
      (el) => !el.classList.contains('info-card-grid-item--hide-desktop-row-solo')
    );

    const tops = rowItems.map((el) => Math.round(el.getBoundingClientRect().top));
    const topCounts = new Map();
    for (const top of tops) topCounts.set(top, (topCounts.get(top) || 0) + 1);

    for (const card of infoCards) {
      const top = Math.round(card.getBoundingClientRect().top);
      const count = topCounts.get(top) || 0;
      // Hide if it is the only item on its row.
      if (count === 1) {
        card.classList.add('info-card-grid-item--hide-desktop-row-solo');
      } else {
        card.classList.remove('info-card-grid-item--hide-desktop-row-solo');
      }
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
